'use client';

import * as React from 'react';
import { useIsomorphicLayoutEffect } from '@/shared/hooks/use-isomorphic-layout-effect';
type RuntimeEnvVariables = Runtime.PublicEnvVariables;
interface RuntimeEnvVariablesProviderProps extends React.PropsWithChildren {
  runtimeEnvVariables: RuntimeEnvVariables;
}
export const EMPTY_RUNTIME_ENV_VARIABLES = ({} as RuntimeEnvVariables);
export const RuntimeEnvVariablesContext = React.createContext<RuntimeEnvVariables>(EMPTY_RUNTIME_ENV_VARIABLES);

/**
 * @useClient
 */
export const RuntimeEnvVariablesProvider = React.memo(function RuntimeEnvVariablesProvider({
  children,
  runtimeEnvVariables
}: RuntimeEnvVariablesProviderProps) {
  const stringifiedRuntimeEnvVars = JSON.stringify(runtimeEnvVariables ?? EMPTY_RUNTIME_ENV_VARIABLES);
  const stableRuntimeEnvVars = React.useMemo<RuntimeEnvVariables>(() => {
    const parsedRuntimeEnvVars = JSON.parse(stringifiedRuntimeEnvVars);
    if (typeof window !== 'undefined') {
      /**
       * @note Since we have a `getRuntimePublicEnvVariable` utility that is used during rendering in some cases,
       * we use this trick and pass the `runtimeEnvVariables` to the global window also during rendering to avoid
       * errors with missing data in the global window.
       */
      console.debug('[RuntimeEnvVariablesProvider]: dirty trick with passing `runtimeEnvVariables` ​​during rendering');
      window.env = {
        ...window?.env,
        ...parsedRuntimeEnvVars
      };
    }
    return parsedRuntimeEnvVars;
  }, [stringifiedRuntimeEnvVars]);
  useIsomorphicLayoutEffect(() => {
    window.env = {
      ...window?.env,
      ...JSON.parse(stringifiedRuntimeEnvVars)
    };
  }, [stringifiedRuntimeEnvVars]);
  return <RuntimeEnvVariablesContext.Provider value={stableRuntimeEnvVars}>{children}</RuntimeEnvVariablesContext.Provider>;
});